import { CreateStageInput, MasterUser, Production, TheaterCompany, UpdateProductionInput, UpdateStageInput, UpdateTheaterCompanyInput } from "@/graphql/types"
import { InputLabel, TextField } from "@mui/material"
import { useCallback, useMemo, useState } from "react"
import Button from "../common/button/button"
import * as GraphQLClient from "@apollo/client"
import { CreateProduction, CreateStage, CreateTheaterCompany, GetOwnTheaterCompanies, UpdateProduction, UpdateStage, UpdateTheaterCompany } from "@/graphql/documentNode"
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar"
import { useQuery } from "react-query"
import styled from "styled-components"
import DateTimePicker from "react-datetime-picker"
import dayjs from "dayjs"
import SelectItem from "../common/input/selectItem"
import Switcher from "../common/switcher/switcher"
import SelectItemV2 from "../common/input/selectItemV2"

type Props = {
    productions: Production[]
    stageData?: UpdateStageInput
    user: MasterUser
    onComplete: () => void
}


const CreateOrUpdateStage = (props: Props) => {
    const snackBar = useSnackBar({})

    const [stageName, setStageName] = useState<string | undefined | null>("")
    const [openDateTime, setOpenDateTime] = useState<number | undefined>(undefined)
    const [startDateTime, setStartDateTime] = useState<number | undefined>(undefined)
    const [endDateTime, setEndDateTime] = useState<number | undefined>(undefined)
    const [productionId, setProductionId] = useState<number | undefined>(undefined)
    const [reservationStart, setReservationStart] = useState<number | undefined>(undefined)
    const [reservationLimit, setReservationLimit] = useState<number | undefined>(undefined)
    const [caution, setCaution] = useState<string | undefined | null>("")
    const [mailMessage, setMailMessage] = useState<string | undefined | null>("")
    const [disableSeatSelect, setDisableSeatSelect] = useState(false)
    



    const nameError = useMemo(() => {
        const texts: string[] = []
        if (!stageName) {
            texts.push("公演名は必須です")
        } else {
            if (stageName.length > 50) {
                texts.push("公演名は50文字以下です")
            }
        }
        return texts
    }, [stageName])

    const cautionError = useMemo(() => {
        const texts: string[] = []
        if(caution) {
            if(caution.length > 300) {
                texts.push("確認事項は300字以内です")
            }
        }
        return texts
    }, [caution])

    const mailMessageError = useMemo(() => {
        const texts: string[] = []
        if(mailMessage) {
            if(mailMessage.length > 2000) {
                texts.push("承認メール連絡事項は2000字以内です")
            }
        }
        return texts
    }, [mailMessage])



    useQuery([props], () => {
        if (props.stageData) {
            setStageName(props.stageData.stageName)
            setOpenDateTime(props.stageData.openDateTime ?? undefined)
            setStartDateTime(props.stageData.startDateTime ?? undefined)
            setEndDateTime(props.stageData.endDateTime ?? undefined)
            setProductionId(props.stageData.productionId ?? undefined)
            setReservationStart(props.stageData.reservationStart ?? undefined)
            setReservationLimit(props.stageData.reservationLimit ?? undefined)
            setCaution(props.stageData.caution ?? undefined)
            setMailMessage(props.stageData.mailMessage ?? undefined)
            setDisableSeatSelect(props.stageData.disableSeatSelect ?? false)
        } else {
            setStageName("")
            setOpenDateTime(undefined)
            setStartDateTime(undefined)
            setEndDateTime(undefined)
            setProductionId(undefined)
            setReservationStart(undefined)
            setReservationLimit(undefined)
            setCaution("")
            setMailMessage("")
            setDisableSeatSelect(false)
        }
    })

    const [createStage] = GraphQLClient.useMutation(CreateStage, {
        onCompleted: (data) => {
            snackBar.onMutationSuccess()
        },
        onError: (err) => {
            console.log(err)
            snackBar.onMutationError()
        },
    })

    const [updateStage] = GraphQLClient.useMutation(UpdateStage, {
        onCompleted: (data) => {
            snackBar.onMutationSuccess()
        },
        onError: (err) => {
            console.log(err)
            snackBar.onMutationError()
        },
    })
    const disableSubmit = useMemo(() => {
        return !!nameError.length || !!cautionError.length || !!mailMessageError.length || !openDateTime || !startDateTime || !endDateTime || !reservationStart || !reservationLimit || (productionId === undefined || productionId < 0)
    }, [cautionError.length, endDateTime, mailMessageError.length, nameError.length, openDateTime, productionId, reservationLimit, reservationStart, startDateTime])

    const submit = useCallback(async () => {
        if (disableSubmit) {
            return
        }
        snackBar.loadStart()
        console.log(props.stageData)
        if (props.stageData) {
            await updateStage({
                variables: {
                    input: {
                        id: props.stageData?.id,
                        productionId,
                        stageName,
                        openDateTime,
                        startDateTime,
                        endDateTime,
                        reservationStart,
                        reservationLimit,
                        caution,
                        mailMessage,
                        disableSeatSelect,
                    }
                },
            })
        } else {
            await createStage({
                variables: {
                    input: {
                        productionId,
                        stageName,
                        openDateTime,
                        startDateTime,
                        endDateTime,
                        reservationStart,
                        reservationLimit,
                        caution,
                        mailMessage,
                        disableSeatSelect,
                    }
                },
            })
        }
        props.onComplete()
    }, [caution, createStage, disableSeatSelect, disableSubmit, endDateTime, mailMessage, openDateTime, productionId, props, reservationLimit, reservationStart, snackBar, stageName, startDateTime, updateStage])

    const selectedProductionIndex = useMemo(() => {
        if(productionId) {
            return props.productions.findIndex(p => p.id === productionId)
        }
        return -1
    }, [productionId, props.productions])

    return (
        <CreateOrUpdateProductionStyle>
            <div className="input-area">
                <div className="input">
                    <InputLabel>公演選択</InputLabel>
                    <SelectItemV2 
                        items={props.productions}
                        selectedIndex={selectedProductionIndex}
                        changeIndex={(item, index) => { setProductionId(item.id) }}
                        width={250}
                    />
                </div>
                <div className="input">
                    <InputLabel>ステージ名</InputLabel>
                    <TextField
                        defaultValue={props.stageData?.stageName}
                        key={props.stageData?.stageName}
                        sx={{ width: "250px" }}
                        variant="standard"
                        placeholder="劇団One-Ahead"
                        onChange={(event) => {
                            setStageName(event.target.value);
                        }}
                        error={!!nameError.length}
                        helperText={nameError}
                    />
                </div>
                <div className="input">
                    <InputLabel>予約開始日時</InputLabel>
                    <DateTimePicker
                        defaultValue={props.stageData?.reservationStart ? dayjs.unix(props.stageData?.reservationStart).toDate() : undefined}
                        key={props.stageData?.reservationStart}
                        className="dateTimePicker"
                        disableClock
                        hourHandLength={2}
                        maxDate={reservationLimit ? dayjs.unix(reservationLimit).toDate() : undefined}
                        value={
                            reservationStart ? dayjs.unix(reservationStart).toDate() : undefined
                        }
                        onChange={(value) => {
                            setReservationStart(dayjs(value).unix());
                        }}
                    />
                </div>
                <div className="input">
                    <InputLabel>予約締切日時</InputLabel>
                    <DateTimePicker
                        defaultValue={props.stageData?.reservationLimit ? dayjs.unix(props.stageData?.reservationLimit).toDate() : undefined}
                        key={props.stageData?.reservationLimit}
                        className="dateTimePicker"
                        disableClock
                        hourHandLength={2}
                        minDate={reservationStart ? dayjs.unix(reservationStart).toDate() : undefined}
                        value={
                            reservationLimit ? dayjs.unix(reservationLimit).toDate() : undefined
                        }
                        onChange={(value) => {
                            setReservationLimit(dayjs(value).unix());
                        }}
                    />
                </div>
                <div className="input">
                    <InputLabel>開場日時</InputLabel>
                    <DateTimePicker
                        defaultValue={props.stageData?.openDateTime ? dayjs.unix(props.stageData?.openDateTime).toDate() : undefined}
                        key={props.stageData?.openDateTime}
                        className="dateTimePicker"
                        disableClock
                        hourHandLength={2}
                        maxDate={startDateTime ? dayjs.unix(startDateTime).toDate() : endDateTime ? dayjs.unix(endDateTime).toDate() : undefined}
                        value={
                            openDateTime ? dayjs.unix(openDateTime).toDate() : undefined
                        }
                        onChange={(value) => {
                            setOpenDateTime(dayjs(value).unix());
                        }}
                    />
                </div>
                <div className="input">
                    <InputLabel>開演日時</InputLabel>
                    <DateTimePicker
                        defaultValue={props.stageData?.startDateTime ? dayjs.unix(props.stageData?.startDateTime).toDate() : undefined}
                        key={props.stageData?.startDateTime}
                        className="dateTimePicker"
                        disableClock
                        hourHandLength={2}
                        minDate={openDateTime ? dayjs.unix(openDateTime).toDate() : undefined}
                        maxDate={endDateTime ? dayjs.unix(endDateTime).toDate() : undefined}
                        value={
                            startDateTime ? dayjs.unix(startDateTime).toDate() : undefined
                        }
                        onChange={(value) => {
                            setStartDateTime(dayjs(value).unix());
                        }}
                    />
                </div>
                <div className="input">
                    <InputLabel>終演日時</InputLabel>
                    <DateTimePicker
                        defaultValue={props.stageData?.endDateTime ? dayjs.unix(props.stageData?.endDateTime).toDate() : undefined}
                        key={props.stageData?.endDateTime}
                        className="dateTimePicker"
                        disableClock
                        hourHandLength={2}
                        minDate={startDateTime ? dayjs.unix(startDateTime).toDate() : openDateTime ? dayjs.unix(openDateTime).toDate() : undefined}
                        value={
                            endDateTime ? dayjs.unix(endDateTime).toDate() : undefined
                        }
                        onChange={(value) => {
                            setEndDateTime(dayjs(value).unix());
                        }}
                    />
                </div>
                <div className="input">
                    <InputLabel>確認事項</InputLabel>
                    <TextField
                        defaultValue={props.stageData?.caution}
                        key={props.stageData?.caution}
                        sx={{ width: "250px" }}
                        variant="standard"
                        placeholder="会場内は禁煙・飲食禁止です。"
                        onChange={(event) => {
                            setCaution(event.target.value);
                        }}
                        error={!!cautionError.length}
                        helperText={cautionError}
                        multiline
                    />
                </div>
                <div className="input">
                    <InputLabel>承認メール記載メッセージ</InputLabel>
                    <TextField
                        defaultValue={props.stageData?.mailMessage}
                        key={props.stageData?.mailMessage}
                        sx={{ width: "250px", whiteSpace: "pre-line", }}
                        variant="standard"
                        placeholder="キャンセルは３日前までです。"
                        onChange={(event) => {
                            setMailMessage(event.target.value);
                        }}
                        error={!!mailMessageError.length}
                        helperText={mailMessageError}
                        multiline
                        rows={5}
                    />
                </div>
                <div className="input">
                    <Switcher 
                        key="switcher"
                        defaultValue={props.stageData?.disableSeatSelect ?? false}
                        baseColor="black"
                        labelOn={"運営が座席を割振"}
                        labelOff={"観客が座席を選択"}
                        setCallback={(value) => { setDisableSeatSelect(value) }}
                    />
                </div>
            </div>
            <div>
                <Button
                    label="送信"
                    buttonType="powerful-red"
                    onClick={submit}
                    disabled={disableSubmit}
                />
            </div>
            {snackBar.component}
        </CreateOrUpdateProductionStyle>
    )
}

const CreateOrUpdateProductionStyle = styled.div`
    .input-area {
        width: 250px;
        margin: auto;
    }
`

export default CreateOrUpdateStage